import React from 'react';
import moment from 'moment';
import { FormattedDate } from 'react-intl';
import { useSelector } from 'react-redux';
import { DateRange, getDayLabels } from './types';
import s, { IN_RANGE_BG, TEXT_DISABLED } from './styles';
import { Colors } from '../../theme';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../redux/general/general-reducer';
import { Locale } from '../../../language';

type CalendarGridProps = {
  month: moment.Moment;
  selectedRange: DateRange;
  hoveredDate: moment.Moment | null;
  today: moment.Moment;
  onDateClick: (date: moment.Moment) => void;
  onDateHover: (date: moment.Moment | null) => void;
  isDateDisabled: (date: moment.Moment) => boolean;
};

const isSameDay = (
  d1: moment.Moment | null | undefined,
  d2: moment.Moment | null | undefined
) => {
  if (!d1 || !d2) return false;
  return d1.isSame(d2, 'day');
};

const CalendarGrid: React.FC<CalendarGridProps> = ({
  month,
  selectedRange,
  hoveredDate,
  today,
  onDateClick,
  onDateHover,
  isDateDisabled,
}) => {
  const lang = useSelector(generalStateSelector(GENERAL_STATE_KEY.LOCALE));
  const dayLabels = getDayLabels(lang === Locale.TH ? 'th' : 'en');
  const firstDayOfWeek = month.clone().startOf('month').day();
  const daysInMonth = month.daysInMonth();

  const isInRange = (date: moment.Moment) => {
    if (!selectedRange?.from) return false;
    const compareDate = selectedRange.to || hoveredDate;
    if (!compareDate) return false;
    const start = selectedRange.from.isBefore(compareDate, 'day') ? selectedRange.from : compareDate;
    const end = selectedRange.from.isBefore(compareDate, 'day') ? compareDate : selectedRange.from;
    return date.isAfter(start, 'day') && date.isBefore(end, 'day');
  };

  const cells: React.ReactNode[] = [];

  for (let i = 0; i < firstDayOfWeek; i++) {
    cells.push(<div key={`empty-${i}`} style={s.cellEmpty} />);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = month.clone().date(day);
    const isSelected =
      isSameDay(currentDate, selectedRange.from) || isSameDay(currentDate, selectedRange.to);
    const inRange = isInRange(currentDate);
    const isDisabled = isDateDisabled(currentDate);
    const showTodayIndicator = !isDisabled && currentDate.isSame(today, 'day') && !isSelected;

    let cellColor: string;
    let cellBg: string | undefined;
    if (isDisabled) {
      cellColor = TEXT_DISABLED;
    } else if (isSelected) {
      cellColor = '#fff';
      cellBg = Colors.rbhViolet600;
    } else if (inRange) {
      cellColor = Colors.rbhViolet600;
      cellBg = IN_RANGE_BG;
    } else if (showTodayIndicator) {
      cellColor = Colors.rbhViolet600;
    } else {
      cellColor = Colors.textPrimary;
    }

    cells.push(
      <button
        key={day}
        type="button"
        disabled={isDisabled}
        onClick={() => onDateClick(currentDate)}
        onMouseEnter={() => !isDisabled && onDateHover(currentDate)}
        onMouseLeave={() => onDateHover(null)}
        className={!isDisabled && !isSelected && !inRange ? 'rbh-drp-v2-cell-btn' : undefined}
        style={{
          ...s.cellBase,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          color: cellColor,
          background: cellBg || 'transparent',
        }}
      >
        {day}
      </button>
    );
  }

  return (
    <div style={s.calendarWrapper}>
      <div style={s.monthName}>
        <FormattedDate value={month.toDate()} month="long" year="numeric" />
      </div>
      <div style={s.dayHeaderGrid}>
        {dayLabels.map((d, i) => (
          <div key={i} style={s.dayHeader}>{d}</div>
        ))}
      </div>
      <div style={s.cellGrid}>{cells}</div>
    </div>
  );
};

export default CalendarGrid;
